import { defaultReminderConfiguration, ReminderConfiguration, reminderConfigurationSchema } from '@procureai/schemas';
import { UseEditableWidgetBackend } from '@procureai/ui-modules-mui';

import {
  ReminderSettingsMutationMutation,
  ReminderSettingsMutationMutationVariables,
  ReminderSettingsQueryQuery,
  ReminderSettingsQueryQueryVariables,
  TsrctReminderConfigVariant,
  TsrctReminderType,
} from '@/generated/gql/graphql';
import { createEditableWidgetGraphqlBackendHook } from '@/shared/hooks/useEditableWidgetGraphqlBackend/useEditableWidgetGraphqlBackend';
import { isSourcingCaseEditable } from '~/common/guards/sourcingCase.guards';
import { ModuleEntity } from '~/common/types';
import { ModuleEntityType } from '~/common/types/ModuleEntity';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { isNegotiationCaseEditable } from '~/util/negotiationCase/isNegotiationCaseEditable';

import { NEGOTIATION_REMINDER_SETTINGS_MUTATION } from './NegotiationReminderSettingsWidget.mutation.gql.ts';
import { NEGOTIATION_REMINDER_SETTINGS_QUERY } from './NegotiationReminderSettingsWidget.query.gql.ts';

const useNegotiationReminderSettingsWidgetBackendInternal = createEditableWidgetGraphqlBackendHook<
  { reminderConfiguration: ReminderConfiguration },
  ReminderSettingsQueryQuery,
  ReminderSettingsQueryQueryVariables,
  ReminderSettingsMutationMutation,
  ReminderSettingsMutationMutationVariables
>({
  schema: reminderConfigurationSchema.transform((reminderConfiguration) => ({ reminderConfiguration })),
  query: NEGOTIATION_REMINDER_SETTINGS_QUERY,
  mutation: NEGOTIATION_REMINDER_SETTINGS_MUTATION,
});

const getReminderConfigVariant = (entity: ModuleEntity): TsrctReminderConfigVariant => {
  switch (entity) {
    case ModuleEntityType.DEMAND:
      return TsrctReminderConfigVariant.DEMAND;
    case ModuleEntityType.NEGOTIATION_CASE:
      return TsrctReminderConfigVariant.NEGOTIATION_CASE;
    case ModuleEntityType.CONTRACT_DEMAND:
      return TsrctReminderConfigVariant.CONTRACT_DEMAND;
    case ModuleEntityType.SOURCING_CASE:
    default:
      return TsrctReminderConfigVariant.SOURCING_CASE;
  }
};

const mapReminderConfiguration = (
  reminderConfig?: ReminderSettingsQueryQuery['tesseract']['reminderConfig'],
): ReminderConfiguration => ({
  reminderDelayWorkingDays:
    reminderConfig?.reminderDelayWorkingDays ?? defaultReminderConfiguration.reminderDelayWorkingDays,
  cancellationDelayWorkingDays:
    reminderConfig?.cancellationDelayWorkingDays ?? defaultReminderConfiguration.cancellationDelayWorkingDays,
  executionTime: reminderConfig?.executionTime ?? defaultReminderConfiguration.executionTime,
  timezone: reminderConfig?.timezone ?? defaultReminderConfiguration.timezone,
});

const isReminderSettingsEditable = (entity: ModuleEntity, data: ReminderSettingsQueryQuery | null): boolean => {
  switch (entity) {
    case ModuleEntityType.NEGOTIATION_CASE:
      return isNegotiationCaseEditable(data?.tesseract.negotiationCase);
    case ModuleEntityType.SOURCING_CASE:
      return isSourcingCaseEditable(data?.tesseract.sourcingCase);
    default:
      return true;
  }
};

export const useNegotiationReminderSettingsWidgetBackend: UseEditableWidgetBackend<{
  reminderConfiguration: ReminderConfiguration;
}> = ({ i18n }) => {
  const { entityId, entity } = useModuleContext();
  const variant = getReminderConfigVariant(entity);
  const variables = {
    targetId: entityId,
    type: TsrctReminderType.SUPPLIER_INACTIVE,
    variant,
    isNegotiationCase: entity === ModuleEntityType.NEGOTIATION_CASE,
    isSourcingCase: entity === ModuleEntityType.SOURCING_CASE,
  };

  return useNegotiationReminderSettingsWidgetBackendInternal({
    i18n,
    variables,
    skip: !entityId,
    toFormData: (data) => {
      return {
        reminderConfiguration: mapReminderConfiguration(data?.tesseract.reminderConfig),
      };
    },
    toMutationData: (formData) => {
      const reminderConfiguration = formData.reminderConfiguration ?? defaultReminderConfiguration;

      return {
        data: {
          targetId: entityId,
          type: TsrctReminderType.SUPPLIER_INACTIVE,
          variant,
          reminderDelayWorkingDays: reminderConfiguration.reminderDelayWorkingDays,
          cancellationDelayWorkingDays: reminderConfiguration.cancellationDelayWorkingDays,
          // executionTime/timezone are optional in the schema but required by the mutation input;
          // fall back to the defaults (the form always renders them with a value).
          executionTime: reminderConfiguration.executionTime ?? defaultReminderConfiguration.executionTime ?? '09:00',
          timezone: reminderConfiguration.timezone ?? defaultReminderConfiguration.timezone ?? 'UTC',
        },
      };
    },
    toSimpleFormWidgetProps: (data, loading) => {
      return loading || !data?.tesseract.reminderConfig || !isReminderSettingsEditable(entity, data)
        ? { disabled: true, loading }
        : { disabled: false, loading };
    },
  });
};
